import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import { Container } from "../Users/styles";
import { Button, Input, TableColumnsType, Tag, Tooltip } from "antd";
import { SearchOutlined, CalendarOutlined, CloseCircleOutlined, CheckCircleFilled } from "@ant-design/icons";
import { convertUtcToLocal } from "../../utils/helpers/convertUtcToLocal";
import { useAppDispatch, useAppSelector } from "../../utils/hooks/redux";
import { selectLogs } from "../../redux/features/logs/logsSlice";
import { getAllPaymentLogs } from "../../redux/features/logs/logsAsyncThunk";
import { PaymentLog } from "../../types/ActivityLog";
import { RequestResults } from "../../redux/common/RequestStatus";
import { ClaimButton, LogsTable } from "../EmailLogs/styles";
import SystemPayRoutes from "../../utils/constants/Routes";
import DatePickerModal from "../../components/DatePickerModal";
import {
  FilterTabWrapper,
  FilterButton,
  CountBadge,
  StatusFilters,
  FilterInputs,
  StyledSelect
} from "../Customers/styles";

dayjs.extend(isBetween);

type StatusFilterKey = 'all' | 'succeeded' | 'failed' | 'refunded';

interface StatusFilterOption {
  key: StatusFilterKey;
  label: string;
  paymentStatus?: number; // maps to backend PaymentStatus enum value
  colors: {
    background: string;
    border: string;
    text: string;
  };
}

const statusFilterOptions: StatusFilterOption[] = [
  {
    key: 'all',
    label: 'All',
    colors: { background: '#ffffff', border: '#d1d5db', text: '#1f2937' }
  },
  {
    key: 'succeeded',
    label: 'Succeeded',
    paymentStatus: 4,
    colors: { background: '#ecfdf5', border: '#10b981', text: '#059669' }
  },
  {
    key: 'failed',
    label: 'Failed',
    paymentStatus: 5,
    colors: { background: '#fef2f2', border: '#ef4444', text: '#dc2626' }
  },
  {
    key: 'refunded',
    label: 'Refunded',
    paymentStatus: 6,
    colors: { background: '#fffbeb', border: '#f59e0b', text: '#d97706' }
  }
];

const getPaymentStatusTag = (status: number): { label: string; color: string; bg: string } => {
  switch (status) {
    case 4: return { label: 'Succeeded', color: '#059669', bg: '#ecfdf5' };
    case 5: return { label: 'Failed', color: '#dc2626', bg: '#fef2f2' };
    case 6: return { label: 'Refunded', color: '#d97706', bg: '#fffbeb' };
    case 1: return { label: 'Pending', color: '#6b7280', bg: '#f3f4f6' };
    case 2: return { label: 'Processing', color: '#2563eb', bg: '#eff6ff' };
    case 3: return { label: 'Canceled', color: '#9ca3af', bg: '#f9fafb' };
    default: return { label: 'Unknown', color: '#9ca3af', bg: '#f9fafb' };
  }
};

const PaymentLogs = () => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [currentLimit, setCurrentLimit] = useState<number>(50);
  const [searchField, setSearchField] = useState<string | undefined>();
  const [searchValue, setSearchValue] = useState<string | undefined>();
  const [statusFilter, setStatusFilter] = useState<StatusFilterKey>("all");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState<string | undefined>();
  const [isDateModalOpen, setIsDateModalOpen] = useState(false);
  const [dateField, setDateField] = useState<string | undefined>();
  const [fromDate, setFromDate] = useState<string | undefined>();
  const [toDate, setToDate] = useState<string | undefined>();
  const { paymentLogs: { data: paymentLogs, status, counts, succeededCount, failedCount, refundedCount, totalCount } } = useAppSelector(selectLogs);
  const dispatch = useAppDispatch();

  const handleSearch = (value: string) => {
    setSearchValue(value);
    setCurrentPage(1);
  };

  const handleSearchFieldChange = (value: string | null) => {
    setSearchField(value || undefined);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => setCurrentPage(page);

  const handleLimitChange = (limit: number) => {
    setCurrentLimit(limit);
    setCurrentPage(1);
  };

  const addKeyProperty = (data: PaymentLog[]) => {
    return data.map((item: PaymentLog) => ({ ...item, key: item.id }));
  };

  const handleClickOnClaimNumber = (record: PaymentLog) => {
    if (record?.itemNumber) {
      window.open(`${SystemPayRoutes.foundReportBase}/${record?.itemNumber}?withDeleted=true`, '_blank');
    }
    else if (record?.trackingId) {
      window.open(`${SystemPayRoutes.reportBase}/${record?.trackingId}?withDeleted=true`, '_blank');
    }
  };

  const handleStatusFilterChange = (key: StatusFilterKey) => {
    setStatusFilter(key);
    setCurrentPage(1);
  };

  const handleDateModalOk = (params: { dateField?: string; fromDate?: string; toDate?: string }) => {
    setDateField(params.dateField);
    setFromDate(params.fromDate);
    setToDate(params.toDate);
    setIsDateModalOpen(false);
    setCurrentPage(1);
  };

  const handleClearDateFilter = () => {
    setDateField(undefined);
    setFromDate(undefined);
    setToDate(undefined);
    setCurrentPage(1);
  };

  const columns: TableColumnsType<any> = useMemo(() => {
    return [
      { title: "#", key: "index", render: (_, __, index) => `#${index + 1 + (currentPage - 1) * currentLimit}`, width: 60 },
      { title: "Claim #", dataIndex: "trackingId", key: "trackingId", render: (_, record: PaymentLog) => <ClaimButton type="link" onClick={() => handleClickOnClaimNumber(record)}>{record?.trackingId || record?.itemNumber || "-"}</ClaimButton>, width: 110 },
      { title: "Amount", dataIndex: "totalPayableAmount", key: "totalPayableAmount", render: (amount, record: any) => <div>{amount ? `$${amount.toFixed(2)}` : "-"}{record?.currency && record.currency !== 'USD' ? ` ${record.currency}` : ''}</div>, width: 100 },
      { title: "Payer", dataIndex: "payerName", key: "payerName", render: (name, record: any) => (
        <div>
          <div>{name || record?.name || "-"}</div>
          {record?.payerEmail && <div style={{ fontSize: 12, color: '#888' }}>{record.payerEmail}</div>}
        </div>
      ), width: 180 },
      { title: "Payment Method", dataIndex: "cardBrand", key: "cardBrand", render: (brand, record: any) => {
        if (brand && record?.cardLastFour) {
          return <div>{brand} •••• {record.cardLastFour}</div>;
        }
        if (record?.paymentMethod) {
          return <div style={{ textTransform: 'capitalize' }}>{record.paymentMethod.replace('_', ' ')}</div>;
        }
        return <div>-</div>;
      }, width: 140 },
      { title: "Order ID", dataIndex: "paymentIntentId", key: "paymentIntentId", render: (id) => id ? <div style={{ fontSize: 11, fontFamily: 'monospace' }}>{id}</div> : <div>-</div>, width: 180 },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        render: (val: number) => {
          const { label, color, bg } = getPaymentStatusTag(val);
          return <Tag style={{ color, backgroundColor: bg, borderColor: color, fontWeight: 600 }}>{label}</Tag>;
        },
        width: 110
      },
      {
        title: "Paid On",
        dataIndex: "createdAt",
        key: "createdAt",
        render: (date) => <div>{date ? convertUtcToLocal(date, "MM/DD/YYYY hh:mm A") : "-"}</div>,
        width: 160
      },
    ];
  }, [currentPage, currentLimit, paymentLogs]);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearchValue(searchValue), 500);
    return () => clearTimeout(handler);
  }, [searchValue]);

  useEffect(() => {
    dispatch(
      getAllPaymentLogs({
        offset: (currentPage - 1) * currentLimit,
        limit: currentLimit,
        searchField,
        searchValue: debouncedSearchValue?.trim(),
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
        dateField: fromDate && toDate ? "payment.createdAt" : "",
        establishmentType: null,
        paymentStatus: statusFilter !== "all" ? statusFilterOptions.find(o => o.key === statusFilter)?.paymentStatus : undefined
      })
    );
  }, [dispatch, currentPage, currentLimit, searchField, debouncedSearchValue, fromDate, toDate, statusFilter]);

  const isDateRangeSelected = useMemo(() => !!(fromDate && toDate), [fromDate, toDate]);

  const formattedDateRange = useMemo(() => {
    return fromDate && toDate
      ? `${dayjs(fromDate).format("MM/DD/YYYY")} - ${dayjs(toDate).format("MM/DD/YYYY")}`
      : "Date Range";
  }, [fromDate, toDate]);

  return (
    <Container>
      <FilterTabWrapper>
        <StatusFilters>
          {statusFilterOptions.map((filter) => {
            const isActive = statusFilter === filter.key;
            const chipTextColor = filter.colors.text;
            const chipBackground = filter.colors.background;

            const getCount = () => {
              switch (filter.key) {
                case 'all': return totalCount;
                case 'succeeded': return succeededCount;
                case 'failed': return failedCount;
                case 'refunded': return refundedCount;
                default: return 0;
              }
            };

            return (
              <FilterButton
                key={filter.key}
                active={isActive}
                onClick={() => handleStatusFilterChange(filter.key)}
                $backgroundColor={filter.colors.background}
                $borderColor={filter.colors.border}
                $textColor={filter.colors.text}
              >
                {isActive && <CheckCircleFilled style={{ marginRight: 4, color: chipBackground }} />}
                <span style={{ fontSize: "14px", fontWeight: 600 }}>{filter.label}</span>
                <CountBadge $textColor={isActive ? "#ffffff" : chipTextColor} className="status-count-badge">
                  {getCount()}
                </CountBadge>
              </FilterButton>
            );
          })}
        </StatusFilters>

        <FilterInputs>
          <StyledSelect
            allowClear
            value={searchField}
            options={[
              { value: "lost.trackingId", label: "Claim #" },
              { value: "payment.payerName", label: "Payer Name" },
              { value: "payment.payerEmail", label: "Payer Email" },
              { value: "payment.paymentIntentId", label: "Order ID" },
              { value: "payment.totalPayableAmount", label: "Amount" },
            ]}
            onChange={(value) => handleSearchFieldChange(value as string | null)}
            placeholder="Filter by"
            size="middle"
          />
          <Input
            placeholder="Search"
            prefix={<SearchOutlined />}
            value={searchValue}
            onChange={(e) => handleSearch(e.target.value)}
            allowClear
            style={{ width: 200 }}
          />
          <Tooltip title={isDateRangeSelected ? formattedDateRange : "Select date range"}>
            <Button
              icon={<CalendarOutlined />}
              onClick={() => setIsDateModalOpen(true)}
              type={isDateRangeSelected ? "primary" : "default"}
              style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4, height: 32, minWidth: 32, padding: "0 8px" }}
            >
              {isDateRangeSelected && (
                <CloseCircleOutlined
                  style={{ marginLeft: 4 }}
                  onClick={(e) => { e.stopPropagation(); handleClearDateFilter(); }}
                />
              )}
            </Button>
          </Tooltip>
        </FilterInputs>
      </FilterTabWrapper>

      <LogsTable
        columns={columns}
        dataSource={addKeyProperty(paymentLogs)}
        loading={status === RequestResults.pending}
        scroll={{ x: 1200 }}
        pagination={{
          current: currentPage,
          total: counts,
          pageSize: currentLimit,
          showSizeChanger: true,
          onChange: handlePageChange,
          onShowSizeChange: (_, size) => handleLimitChange(size),
        }}
      />

      <DatePickerModal
        isModalOpen={isDateModalOpen}
        handleOk={handleDateModalOk}
        handleCancel={() => setIsDateModalOpen(false)}
        values={{ dateField, fromDate, toDate }}
      />
    </Container>
  );
};

export default PaymentLogs;
